if (!customElements.get('premium-sticky-bar')) {
  customElements.define(
    'premium-sticky-bar',
    class PremiumStickyBar extends HTMLElement {
      constructor() {
        super();
        this.slides = [];
        this.index = 0;
        this.holdTimer = null;
        this.animTimer = null;
        this.paused = false;
        this.userPaused = false;
        this.animating = false;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      connectedCallback() {
        this.stage = this.querySelector('[data-psb-stage]');
        this.slides = Array.from(this.querySelectorAll('[data-psb-slide]'));
        this.pauseBtns = Array.from(this.querySelectorAll('[data-psb-pause]'));
        this.interval = Math.max(2000, Number(this.dataset.interval) || 5000);
        this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reduceMotion;

        this.setStickyOffset();
        this.resizeObserver = new ResizeObserver(() => this.setStickyOffset());
        this.resizeObserver.observe(this);

        if (this.slides.length > 1) {
          this.slides.forEach((slide, i) => {
            slide.hidden = i !== this.index;
            slide.classList.toggle('is-active', i === this.index);
          });
        }

        this.addEventListener('click', this.onPauseClick);

        document.addEventListener('visibilitychange', this.onVisibility);
        window.addEventListener('resize', this.onResize);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

        this.start();
      }

      disconnectedCallback() {
        this.stop();
        this.resizeObserver?.disconnect();
        this.removeEventListener('click', this.onPauseClick);
        document.removeEventListener('visibilitychange', this.onVisibility);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.documentElement.style.removeProperty('--pab-sticky-offset');
      }

      onPauseClick = (event) => {
        if (!event.target.closest('[data-psb-pause]')) return;
        event.preventDefault();
        event.stopPropagation();
        this.userPaused = !this.userPaused;
        if (this.userPaused) {
          this.pause();
        } else {
          this.resume();
        }
        this.syncPauseButton();
      };

      onVisibility = () => {
        if (document.hidden) {
          this.pause();
        } else if (!this.userPaused) {
          this.resume();
        }
      };

      onResize = () => {
        this.setStickyOffset();
      };

      onBlockSelect = (event) => {
        if (!this.contains(event.target)) return;
        const slide = event.target?.closest?.('[data-psb-slide]') || event.target;
        const index = this.slides.indexOf(slide);
        if (index < 0) return;
        this.goTo(index, true);
        this.userPaused = true;
        this.pause();
        this.syncPauseButton();
      };

      onBlockDeselect = (event) => {
        if (!this.contains(event.target)) return;
        this.userPaused = false;
        this.resume();
        this.syncPauseButton();
      };

      start() {
        this.syncPauseButton();
        this.schedule();
      }

      schedule() {
        this.clearHold();
        if (!this.autoplay || this.userPaused || this.paused || this.animating) return;
        this.holdTimer = window.setTimeout(() => this.next(), this.interval);
      }

      clearHold() {
        if (this.holdTimer) {
          window.clearTimeout(this.holdTimer);
          this.holdTimer = null;
        }
      }

      clearAnim() {
        if (this.animTimer) {
          window.clearTimeout(this.animTimer);
          this.animTimer = null;
        }
      }

      stop() {
        this.clearHold();
        this.clearAnim();
        this.animating = false;
      }

      pause() {
        this.paused = true;
        this.clearHold();
        this.syncPauseButton();
      }

      resume() {
        this.paused = false;
        this.schedule();
        this.syncPauseButton();
      }

      next() {
        this.goTo((this.index + 1) % this.slides.length);
      }

      goTo(nextIndex, instant = false) {
        if (!this.slides.length || nextIndex === this.index) return;
        if (this.animating && !instant) return;

        const current = this.slides[this.index];
        const next = this.slides[nextIndex];
        if (!current || !next) return;

        const duration = Number(this.dataset.transition) || 1500;

        this.clearHold();
        this.clearAnim();

        if (instant || this.reduceMotion) {
          current.hidden = true;
          current.classList.remove('is-active', 'is-leaving', 'is-from-side');
          next.hidden = false;
          next.classList.remove('is-leaving', 'is-from-side');
          next.classList.add('is-active');
          this.index = nextIndex;
          this.animating = false;
          this.setStickyOffset();
          this.schedule();
          return;
        }

        this.animating = true;
        next.hidden = false;
        next.classList.add('is-from-side');
        void next.offsetWidth;

        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        next.classList.remove('is-from-side');
        next.classList.add('is-active');
        this.index = nextIndex;

        this.animTimer = window.setTimeout(() => {
          current.hidden = true;
          current.classList.remove('is-leaving', 'is-from-side');
          this.animating = false;
          this.setStickyOffset();
          this.schedule();
        }, duration);
      }

      syncPauseButton() {
        const paused = this.userPaused || this.paused;
        const label = paused
          ? this.dataset.playLabel || 'Play announcements'
          : this.dataset.pauseLabel || 'Pause announcements';
        this.pauseBtns.forEach((btn) => {
          btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
          btn.setAttribute('aria-label', label);
        });
        this.classList.toggle('is-paused', paused);
      }

      setStickyOffset() {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const stickyHere = isMobile
          ? this.dataset.stickyMobile === 'true'
          : this.dataset.stickyDesktop === 'true';
        const measured = Math.round(this.getBoundingClientRect().height);
        const height = stickyHere && measured > 0 ? measured : 0;
        document.documentElement.style.setProperty('--pab-sticky-offset', `${height}px`);
        document.documentElement.style.setProperty('--pab-height', `${measured}px`);
      }
    }
  );
}
